<template>
    <div class="company-detail-root">
        <Header :label="data().name" />

        <MainLayout leftToBottomRight marginTop hideOnMobile="left">
            <ProfileModule slot="left" />
            <FeaturedResource slot="left" />
            <WidgetModule slot="left" />

            <Presentation
                slot="center"
                :description="data().description"
                :companyName="data().name"
                :chatUsers="chatUsers()"
                :isLoading="loading() || chatUsersLoading()"
                :isPosterLoading="isPosterLoading()"
                :poster="banner"
            />

            <Locations slot="center" :items="locations" />

            <JobModule
                key="123"
                slot="center"
                :companyName="data().name"
                :companyId="companyIds()"
                redirectUsingQuery
            />

            <CompanyModule :id="this.$route.params.id" slot="right" />

            <Paper v-if="data().gptw" slot="right" tall customPadding="px-3 pt-4 pb-2">
                <b-img
                    v-b-tooltip.hover
                    rounded
                    :alt="data().gptw.text"
                    :src="data().gptw.img"
                    :title="data().gptw.tooltip"
                    height="60"
                    class="ml-2 mb-2"
                    style="float: right"
                />
                <h4>CERTIFIED GREAT PLACE TO WORK</h4>
                <p class="mb-0">
                    Minim mollit adipisicing dolor cupidatat fugiat Lorem sunt. Adipisicing anim
                    eiusmod cillum sunt sint. Amet proident aute do do veniam cupidatat magna anim
                    qui aliqua.
                </p>
                <div class="d-flex justify-content-center">
                    <p class="mb-0 p-2 clickable" @click="toAboutGPTW">
                        Read more
                    </p>
                </div>
            </Paper>

            <Videos slot="right" :items="videos" :orgId="data().id" />

            <Articles slot="right" :items="articles" />
        </MainLayout>
    </div>
</template>

<script>
// Project components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import CompanyModule from '~/components/smart/CompanyModule';
import FeaturedResource from '~/components/smart/FeaturedResource';

// Page specific components
import Presentation from './components/Presentation';
import Videos from './components/Videos';
import Articles from './components/Articles';
import JobModule from './components/JobModule';
import Locations from './components/Locations';

// Utils
import AnalyticService from '~/services/AnalyticService';
import SchoolService from '~/services/SchoolService';

import * as Mappers from '~/utils/mappers/mediaMappers';

import { SHOW_CHAT_IN_COMPANY_PROFILE } from '~/router';
import WidgetModule from '~/components/smart/WidgetModule.vue';

export default {
    name: 'CompanyDetail',
    components: {
        Header,
        MainLayout,
        ProfileModule,
        Presentation,
        Videos,
        Articles,
        CompanyModule,
        JobModule,
        Locations,
        FeaturedResource,
        WidgetModule,
    },
    data() {
        return {
            forceUpdate: true,

            hasLoggedEvent: false, // For indicating if the company-view has been logged or not
            schoolId: SchoolService.getCurrentSchoolId(),
            now: 0,
            chatAvailabilityIntervalSeconds: 5,
            chatAvailabilityInterval: null,
        };
    },
    computed: {
        company() {
            const shouldUpdate = this.forceUpdate;

            // TODO: Fix this side effect caused by "forceUpdate"
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.forceUpdate = false;
            return this.$interfaces.company
                .id(this.$route.params.id)
                .get({ reactive: true, forceUpdate: shouldUpdate });
        },
        availableChatUsers() {
            return SHOW_CHAT_IN_COMPANY_PROFILE
                ? this.$interfaces.communication.chat.availability.id
                      .org_id(this.$route.params.id)
                      .get({ reactive: true })
                : [];
        },
        orgProfile() {
            if (this.company.loading) {
                return { loading: true };
            }
            let orgProfile = this.$interfaces.organization.profile
                .id(this.company.data.externalId)
                .get({ reactive: true, schoolId: this.schoolId });
            return orgProfile;
        },
        locations() {
            if (this.orgProfile.loading) return;
            return Mappers.mapLocations(this.orgProfile.data.locations);
        },
        videos() {
            if (this.orgProfile.loading) return;
            return Mappers.mapVideos(this.orgProfile.data.videos, this.orgProfile.data.id);
        },
        articles() {
            if (this.orgProfile.loading) return;
            return Mappers.mapArticles(this.orgProfile.data.articles);
        },
        organizationData() {
            return this.$interfaces.organization.profile.banner.id(this.$route.params.id).get({
                reactive: true,
            });
        },
        banner() {
            if (this.organizationData.loading) return;
            return this.organizationData.data.banner;
        },
        locationIds() {
            return (this.locations || []).map(it => it.id);
        },
    },
    methods: {
        data() {
            return this.company.data || {};
        },
        loading() {
            return this.company.loading;
        },
        chatUsers() {
            if (!this.availableChatUsers.data) return [];
            return this.availableChatUsers.data
                .filter(cu => cu.availableUntil > this.now)
                .map(item => ({
                    ...item,
                    companyId: this.data().id,
                    companyTitle: this.data().name,
                }));
        },
        chatUsersLoading() {
            return SHOW_CHAT_IN_COMPANY_PROFILE ? this.availableChatUsers.loading : false;
        },
        isPosterLoading() {
            return this.organizationData.loading;
        },
        error() {
            return this.company.error;
        },
        companyIds() {
            return this.locationIds && this.locationIds.length > 0
                ? [...this.locationIds, this.data().id]
                : this.data().id;
        },
        logPageviewEvent() {
            // This triggers an Google Analytics event
            const data = this.data();
            if (data.id) {
                AnalyticService.companyViewEvent(data.id, data.name);
                this.hasLoggedEvent = true;
            }
        },
        toAboutGPTW() {
            this.$router.push(this.$URLS.aboutGPTW);
        },
        chatAvailabilityTick() {
            this.now += this.chatAvailabilityIntervalSeconds;
        },
    },
    mounted() {
        window.scrollTo(0, 0);

        if (SHOW_CHAT_IN_COMPANY_PROFILE) {
            this.$interfaces.communication.chat.availability.id
                .org_id(this.$route.params.id)
                .get({ forceUpdate: true });
            this.now = Math.round(new Date().getTime() / 1000);
            this.chatAvailabilityInterval = setInterval(
                this.chatAvailabilityTick,
                this.chatAvailabilityIntervalSeconds * 1000
            );
        }
    },
    beforeDestroy() {
        if (SHOW_CHAT_IN_COMPANY_PROFILE) {
            clearInterval(this.chatAvailabilityInterval);
            this.chatAvailabilityInterval = null;
        }
    },
    watch: {
        company(newCompany, oldCompany) {
            // Log company-pageview if it has not been done yet.
            // If you click onto a new company from the this page, we also have to check
            // if the company-ids have updated, so we can log again.
            if (!this.hasLoggedEvent || newCompany.ids !== oldCompany.ids) {
                this.logPageviewEvent();
            }
        },
    },
};
</script>

<style scoped>
.company-detail-root {
    padding-bottom: 100px;
}
</style>
